import { useMemo, useState } from 'react';
import { Plus, Trash2, UsersRound } from 'lucide-react';
import { resolveLegacyClassId, useSchoolClasses } from '../../../core/school/classes';

const emptyChild = () => ({
  temporaryId: crypto.randomUUID?.() || `child_${Date.now()}_${Math.random()}`,
  fullName: '',
  birthDate: '',
  requestedClassId: '',
  requestedLevel: '',
  previousSchool: '',
  alreadyEnrolled: false,
  hasSiblingsAtSchool: false,
  difficulties: '',
  notes: ''
});

const initialGuardian = {
  fullName: '',
  phone: '',
  email: '',
  cin: '',
  profession: '',
  relationship: 'mother',
  address: ''
};

export default function FamilyRegistrationForm({
  mode = 'internal',
  initialValue,
  submitting = false,
  onSubmit,
  onCancel
}) {
  const { classes } = useSchoolClasses();
  const [guardian, setGuardian] = useState(initialValue?.guardian || initialGuardian);
  const [children, setChildren] = useState(() => initialValue?.children?.length ? initialValue.children.map((child) => ({ ...child, requestedClassId: child.requestedClassId || child.classId || resolveLegacyClassId(child.requestedLevel) })) : [emptyChild()]);
  const [message, setMessage] = useState(initialValue?.message || '');

  const valid = useMemo(() => (
    guardian.fullName.trim() && guardian.phone.trim() &&
    children.length > 0 && children.every((child) => child.fullName.trim() && child.requestedClassId)
  ), [guardian, children]);

  function updateChild(id, field, value) {
    setChildren((current) => current.map((child) => child.temporaryId === id ? { ...child, [field]: value } : child));
  }

  function removeChild(id) {
    setChildren((current) => current.length === 1 ? current : current.filter((child) => child.temporaryId !== id));
  }

  function submit(event) {
    event.preventDefault();
    if (!valid || submitting) return;
    onSubmit?.({
      guardian: {
        ...guardian,
        fullName: guardian.fullName.trim(),
        phone: guardian.phone.trim(),
        email: guardian.email.trim(),
        cin: guardian.cin.trim(),
        profession: guardian.profession.trim(),
        address: guardian.address.trim()
      },
      children: children.map((child) => ({
        ...child,
        fullName: child.fullName.trim(),
        requestedClassId: child.requestedClassId || '',
        requestedLevel: child.requestedLevel.trim(),
        previousSchool: child.previousSchool.trim(),
        difficulties: child.difficulties.trim(),
        notes: child.notes.trim()
      })),
      message: message.trim(),
      source: mode === 'public' ? 'public' : 'internal'
    });
  }

  return <form className="family-form" onSubmit={submit}>
    <section className="family-section">
      <div className="family-section-title">
        <div><span className="family-step">1</span><div><h3>Responsable principal</h3><p>Un seul responsable peut être lié à plusieurs enfants.</p></div></div>
      </div>
      <div className="family-grid two">
        <label>Nom et prénom<input value={guardian.fullName} onChange={(event) => setGuardian({ ...guardian, fullName: event.target.value })} required/></label>
        <label>Téléphone<input value={guardian.phone} onChange={(event) => setGuardian({ ...guardian, phone: event.target.value })} required/></label>
        <label>Email<input type="email" value={guardian.email} onChange={(event) => setGuardian({ ...guardian, email: event.target.value })}/></label>
        <label>CIN<input value={guardian.cin} onChange={(event) => setGuardian({ ...guardian, cin: event.target.value })}/></label>
        <label>Profession<input value={guardian.profession} onChange={(event) => setGuardian({ ...guardian, profession: event.target.value })}/></label>
        <label>Lien avec l’enfant<select value={guardian.relationship} onChange={(event) => setGuardian({ ...guardian, relationship: event.target.value })}><option value="mother">Mère</option><option value="father">Père</option><option value="guardian">Tuteur légal</option><option value="other">Autre</option></select></label>
      </div>
      <label>Adresse<input value={guardian.address} onChange={(event) => setGuardian({ ...guardian, address: event.target.value })}/></label>
    </section>

    <section className="family-section">
      <div className="family-section-title">
        <div><span className="family-step">2</span><div><h3>Enfants</h3><p>Ajoutez tous les enfants concernés par cette inscription familiale.</p></div></div>
        <button type="button" className="button button-secondary" onClick={() => setChildren((current) => [...current, emptyChild()])}><Plus size={16}/> Ajouter un enfant</button>
      </div>
      <div className="children-editor">
        {children.map((child, index) => <article className="child-editor-card" key={child.temporaryId}>
          <header><div><UsersRound size={18}/><strong>Enfant {index + 1}</strong></div>{children.length > 1 && <button type="button" onClick={() => removeChild(child.temporaryId)} aria-label="Retirer"><Trash2 size={17}/></button>}</header>
          <div className="family-grid two">
            <label>Nom et prénom<input value={child.fullName} onChange={(event) => updateChild(child.temporaryId, 'fullName', event.target.value)} required/></label>
            <label>Niveau demandé<select value={child.requestedClassId || ''} onChange={(event) => { const classId = event.target.value; const selectedClass = classes.find((item) => item.id === classId); updateChild(child.temporaryId, 'requestedClassId', classId); updateChild(child.temporaryId, 'requestedLevel', selectedClass?.name || ''); }} required><option value="">Choisir un niveau</option>{classes.map((schoolClass) => <option key={schoolClass.id} value={schoolClass.id}>{schoolClass.name || schoolClass.id}</option>)}</select></label>
            <label>Date de naissance<input type="date" value={child.birthDate} onChange={(event) => updateChild(child.temporaryId, 'birthDate', event.target.value)}/></label>
            <label>Établissement précédent<input value={child.previousSchool} onChange={(event) => updateChild(child.temporaryId, 'previousSchool', event.target.value)}/></label>
          </div>
          <div className="family-checks">
            <label><input type="checkbox" checked={child.alreadyEnrolled} onChange={(event) => updateChild(child.temporaryId, 'alreadyEnrolled', event.target.checked)}/> Déjà inscrit au Défi</label>
            <label><input type="checkbox" checked={child.hasSiblingsAtSchool} onChange={(event) => updateChild(child.temporaryId, 'hasSiblingsAtSchool', event.target.checked)}/> Frère ou sœur déjà inscrit</label>
          </div>
          <label>Difficultés ou besoins particuliers<textarea value={child.difficulties} onChange={(event) => updateChild(child.temporaryId, 'difficulties', event.target.value)} placeholder="Facultatif"/></label>
        </article>)}
      </div>
    </section>

    <section className="family-section">
      <div className="family-section-title"><div><span className="family-step">3</span><div><h3>Message complémentaire</h3><p>Informations utiles pour le suivi administratif.</p></div></div></div>
      <label>Message<textarea value={message} onChange={(event) => setMessage(event.target.value)}/></label>
    </section>

    <footer className="family-form-footer">
      {onCancel && <button type="button" className="button button-secondary" onClick={onCancel}>Annuler</button>}
      <button type="submit" className="button button-primary" disabled={!valid || submitting}>{submitting ? 'Enregistrement…' : mode === 'public' ? 'Envoyer la demande' : 'Créer la demande familiale'}</button>
    </footer>
  </form>;
}
